import { _isNil, _isNotBlank, _notNil } from '@/littledash';
import { DateUtils } from '@/utils/Date.utils';
import { type BaseValidator, type CellProperties, type CellValue } from '@/support/hot.types';

export const InVivoValidatorField = Symbol('in-vivo-validator');
export type InVivoValidatorResult = { valid: true } | { valid: false; message: string };
export type InVivoValidator = (value: CellValue, columnSettings: Record<string, any>) => Promise<InVivoValidatorResult>;
export type HybridValidator = BaseValidator & { [InVivoValidatorField]: InVivoValidator };
export const isHybridValidator = (validator: BaseValidator): validator is HybridValidator =>
  Object.prototype.hasOwnProperty.call(validator, InVivoValidatorField) &&
  typeof (validator as HybridValidator)[InVivoValidatorField] === 'function';

const InVivoValidatorResultField = Symbol('in-vivo-validator-result');

interface RecordedResult {
  value: CellValue;
  result: InVivoValidatorResult;
}

const recordResult = (cellProperties: unknown, value: CellValue, result: InVivoValidatorResult): void => {
  if (_notNil(cellProperties)) {
    (cellProperties as Record<symbol, RecordedResult>)[InVivoValidatorResultField] = { value, result };
  }
};

/**
 * The outcome of the most recent {@link InVivoValidator} run for a cell, recorded on the cell's
 * meta by {@link fromInVivoValidator}.
 *
 * Handsontable's `afterValidate` hook reports only *whether* a cell passed, never why. Reading the
 * recorded outcome recovers the reason without running the validator a second time — a re-run
 * would repeat any request the validator makes.
 *
 * Returns undefined when the cell has not been validated, or was last validated against a
 * different value, so a stale reason can never be attributed to the current one.
 */
export const readInVivoValidatorResult = (
  cellProperties: unknown,
  value: CellValue
): InVivoValidatorResult | undefined => {
  if (_isNil(cellProperties)) {
    return undefined;
  }
  const recorded = (cellProperties as Record<symbol, RecordedResult | undefined>)[InVivoValidatorResultField];
  return _notNil(recorded) && recorded.value === value ? recorded.result : undefined;
};

export const fromInVivoValidator = (
  inVivoValidator: InVivoValidator
): BaseValidator & { [InVivoValidatorField]: InVivoValidator } => {
  const validator = function (this: CellProperties, value: CellValue, callback: (valid: boolean) => void) {
    inVivoValidator(value, this)
      .then((result) => {
        // Recorded before the callback, so a listener reacting to it can read the reason.
        recordResult(this, value, result);
        callback(result.valid);
      })
      .catch(() => callback(false));
  };
  return Object.assign(validator, { [InVivoValidatorField]: inVivoValidator });
};

export const textMetadataInVivoValidator: InVivoValidator = async (
  value: CellValue,
  columnSettings: Record<string, any>
) => {
  const required = columnSettings.required === true;
  const stringValue = typeof value === 'string' ? value : (value?.toString() ?? '');
  const hasValue = _isNotBlank(stringValue);

  if (required && !hasValue) {
    return { valid: false, message: 'This field is required' };
  }

  if (hasValue && stringValue.length > 250) {
    return { valid: false, message: 'Must have a max length of 250 characters' };
  }

  return { valid: true };
};

export const dateMetadataInVivoValidator: InVivoValidator = async (
  value: CellValue,
  columnSettings: Record<string, any>
) => {
  const required = columnSettings.required === true;
  const stringValue = `${value ?? ''}`;
  const hasValue = _isNotBlank(stringValue);

  if (required && !hasValue) {
    return { valid: false, message: 'This field is required' };
  }

  if (hasValue && !DateUtils.isValidDate(stringValue)) {
    return { valid: false, message: 'Must be a valid date' };
  }

  return { valid: true };
};

export const numberMetadataInVivoValidator: InVivoValidator = async (
  value: CellValue,
  columnSettings: Record<string, any>
) => {
  const required = columnSettings.required === true;

  if (typeof value === 'number') {
    if (!Number.isFinite(value)) {
      return { valid: false, message: 'Must be a valid number' };
    }
    return { valid: true };
  }

  const stringValue = `${value ?? ''}`;
  const hasValue = _isNotBlank(stringValue);

  if (required && !hasValue) {
    return { valid: false, message: 'This field is required' };
  }

  if (hasValue && !Number.isFinite(Number(stringValue))) {
    return { valid: false, message: 'Must be a valid number' };
  }

  return { valid: true };
};
